const STORAGE_KEY = 'nm.mobileLiquiditySession'
const MAX_AGE_MS = 86_400_000
export const MOBILE_LIQUIDITY_PENDING_MAX_AGE_MS = 15 * 60_000
export const MOBILE_LIQUIDITY_SESSION_EVENT = 'nm:mobile-liquidity-session'

export type MobileLiquidityOperationKind = 'add' | 'remove'

export type MobileLiquiditySessionPhase = 'review' | 'wallet' | 'submitted' | 'confirmed' | 'failed'

export type MobileLiquiditySession = {
  kind: MobileLiquidityOperationKind
  chainId: number
  account: string
  token0: string
  token1: string
  hash?: string
  summary?: string
  phase: MobileLiquiditySessionPhase
  dismissed?: boolean
  updatedAt: number
}

function readRaw(): MobileLiquiditySession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as MobileLiquiditySession
    if (!parsed?.account || !parsed?.chainId || !parsed?.token0 || !parsed?.token1) return null
    return parsed
  } catch {
    return null
  }
}

export function readMobileLiquiditySession(
  kind?: MobileLiquidityOperationKind,
): MobileLiquiditySession | null {
  const session = readRaw()
  if (!session) return null
  if (session.dismissed) return null
  if (kind && session.kind !== kind) return null
  if (Date.now() - session.updatedAt > MAX_AGE_MS) {
    clearMobileLiquiditySession()
    return null
  }
  return session
}

export function writeMobileLiquiditySession(session: MobileLiquiditySession): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    window.dispatchEvent(new CustomEvent(MOBILE_LIQUIDITY_SESSION_EVENT))
  } catch {
    /* quota / private mode */
  }
}

export function patchMobileLiquiditySession(
  patch: Partial<MobileLiquiditySession>,
): MobileLiquiditySession | null {
  const current = readRaw()
  if (!current) return null
  const next: MobileLiquiditySession = {
    ...current,
    ...patch,
    updatedAt: Date.now(),
  }
  writeMobileLiquiditySession(next)
  return next
}

export function clearMobileLiquiditySession(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function isStaleMobileLiquidityPendingSession(
  session: MobileLiquiditySession,
  now = Date.now(),
): boolean {
  if (session.hash) return false
  if (session.phase !== 'wallet' && session.phase !== 'submitted') return false
  return now - session.updatedAt > MOBILE_LIQUIDITY_PENDING_MAX_AGE_MS
}

export function isMobileLiquidityWalletWaitActive(
  session: MobileLiquiditySession | null = readMobileLiquiditySession(),
): boolean {
  if (!session || session.dismissed) return false
  if (session.hash) return false
  if (session.phase !== 'wallet') return false
  if (isStaleMobileLiquidityPendingSession(session)) return false
  return true
}

export function abortMobileLiquidityWalletSession(): void {
  const session = readRaw()
  if (!session) return
  if (session.hash) return
  if (session.phase !== 'wallet' && session.phase !== 'submitted') return
  clearMobileLiquiditySession()
}

export function completeMobileLiquiditySession(): void {
  clearMobileLiquiditySession()
}

export function buildLiquiditySessionSummary(kind: MobileLiquidityOperationKind, token0: string, token1: string): string {
  return kind === 'add' ? `Add liquidity ${token0}/${token1}` : `Remove liquidity ${token0}/${token1}`
}
